// pages/entry-form/CostInfoPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Chip, Dialog,
  DialogContent, DialogActions, CircularProgress, Alert, Tooltip, alpha,
  Fade, Card, Stack, Autocomplete, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, AccountBalance as CostIcon, Close as CloseIcon,
  CalendarToday as CalendarIcon, Category as CategoryIcon,
  AttachMoney as MoneyIcon, TextFields as LabelIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import {
  tradesService,
  CostInfo, CostInfoPayload, CostCategory,
} from '../../services/tradesService';

// ── Accent colour for this page ───────────────────────────────────────────────
const ACCENT = '#9C27B0';

// ── Permission helper (identical pattern to PurchaseInfoPage) ─────────────────
const usePermissions = () => {
  const { user } = useAuth();
  const p = user?.role?.permissions?.[0];
  return {
    canCreate: p?.create_permission ?? false,
    canUpdate: p?.update_permission ?? false,
    canDelete: p?.delete_permission || user?.role?.role_name === 'DATA_MANAGER' || user?.is_superuser,
  };
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (v: string | number) =>
  Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const today = () => new Date().toISOString().split('T')[0];

/** Convert any incoming date string (DD-MM-YYYY or YYYY-MM-DD) → YYYY-MM-DD for <input type="date"> */
const formatDateForInput = (dateStr: string | null | undefined): string => {
  if (!dateStr) return today();
  // Already in YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.slice(0, 10);
  // DD-MM-YYYY (backend datetime format)
  const match = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;
  return today();
};

/** Display date as DD-Mon-YYYY in the table */
const formatDateDisplay = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00');   // avoid UTC-shift
  const day   = String(d.getDate()).padStart(2, '0');
  const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
  return `${day}-${month}-${d.getFullYear()}`;
};

// ══════════════════════════════════════════════════════════════════════════════
// Form Dialog
// ══════════════════════════════════════════════════════════════════════════════
interface FV {
  cost_date:     string;
  cost_category: CostCategory | null;
  cost_name:     string;
  cost_amount:   string;
  remarks:       string;
}

const CostFormDialog: React.FC<{
  open:       boolean;
  editing:    CostInfo | null;
  categories: CostCategory[];
  onClose:    () => void;
  onSaved:    () => void;
}> = ({ open, editing, categories, onClose, onSaved }) => {
  const [saving, setSaving]     = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    control, handleSubmit, reset, formState: { errors },
  } = useForm<FV>({
    defaultValues: {
      cost_date: today(), cost_category: null,
      cost_name: '', cost_amount: '', remarks: '',
    },
  });

  // Populate form when opening
  useEffect(() => {
    if (!open) return;
    setApiError('');
    if (editing) {
      const cat = categories.find(c => c.cost_category_id === editing.cost_category) ?? null;
      reset({
        cost_date:     formatDateForInput(editing.cost_date),
        cost_category: cat,
        cost_name:     editing.cost_name,
        cost_amount:   editing.cost_amount,
        remarks:       editing.remarks || '',
      });
    } else {
      reset({ cost_date: today(), cost_category: null, cost_name: '', cost_amount: '', remarks: '' });
    }
  }, [open, editing, categories, reset]);

  const onSubmit = async (data: FV) => {
    setSaving(true); setApiError('');
    try {
      const payload: CostInfoPayload = {
        cost_date:     data.cost_date,
        cost_category: data.cost_category?.cost_category_id ?? null,
        cost_name:     data.cost_name.trim(),
        cost_amount:   parseFloat(data.cost_amount || '0'),
        remarks:       data.remarks.trim() || undefined,
      };
      if (editing) await tradesService.updateCost(editing.cost_info_id, payload);
      else         await tradesService.createCost(payload);
      onSaved(); onClose();
    } catch (e: any) {
      const errs = e?.data?.errors;
      setApiError(
        errs?.cost_name?.[0] || errs?.cost_amount?.[0] ||
        e?.data?.message || 'Something went wrong.'
      );
    } finally { setSaving(false); }
  };

  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
    >
      {/* Gradient header — matches PurchaseInfoPage style */}
      <Box sx={{
        background: `linear-gradient(135deg, ${ACCENT} 0%, #6A1B9A 100%)`,
        px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CostIcon sx={{ color: 'white', fontSize: 22 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
            {editing ? 'Edit Cost Entry' : 'New Cost Entry'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ px: 3, py: 3 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{apiError}</Alert>
          )}
          <Stack spacing={2.5}>
            
            <Controller name="cost_date" control={control} rules={{ required: 'Date is required', max: { value: new Date().toISOString().split('T')[0], message: 'Future date not allowed' } }}
              render={({ field }) => (
                <TextField
                  {...field} fullWidth label="Cost Date *" type="date"
                  error={!!errors.cost_date} helperText={errors.cost_date?.message}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: new Date().toISOString().split('T')[0] }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon fontSize="small" sx={{ color: ACCENT }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />
            {/* Cost Category */}
            <Controller name="cost_category" control={control}
              render={({ field }) => (
                <Autocomplete
                  options={categories.filter(c => c.is_active)}
                  getOptionLabel={o => o.cost_category_name}
                  value={field.value}
                  onChange={(_, v) => field.onChange(v)}
                  renderInput={params => (
                    <TextField
                      {...params} label="Cost Category"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <CategoryIcon fontSize="small" sx={{ color: ACCENT }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              )}
            />

            {/* Cost Name */}
            <Controller name="cost_name" control={control}
              rules={{ required: 'Cost name is required' }}
              render={({ field }) => (
                <TextField
                  {...field} fullWidth label="Cost Name *"
                  placeholder="e.g. Office rent, Transport, Utilities"
                  error={!!errors.cost_name} helperText={errors.cost_name?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LabelIcon fontSize="small" sx={{ color: ACCENT }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />

            {/* Cost Amount */}
            <Controller name="cost_amount" control={control}
              rules={{
                required: 'Amount is required',
                min: { value: 0, message: 'Amount must be ≥ 0' },
              }}
              render={({ field }) => (
                <TextField
                  {...field} fullWidth label="Cost Amount *" type="number"
                  inputProps={{ step: '0.01', min: 0 }}
                  error={!!errors.cost_amount} helperText={errors.cost_amount?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">Tk. </InputAdornment>,
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />

            {/* Remarks */}
            <Controller name="remarks" control={control}
              render={({ field }) => (
                <TextField
                  {...field} fullWidth label="Remarks" multiline rows={2}
                  placeholder="Optional notes..."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />

          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} disabled={saving} variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, background: `linear-gradient(135deg, ${ACCENT} 0%, #6A1B9A 100%)` }}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Delete Confirm Dialog
// ══════════════════════════════════════════════════════════════════════════════
const DeleteDialog: React.FC<{
  open:      boolean;
  id:        number | null;
  onClose:   () => void;
  onDeleted: () => void;
}> = ({ open, id, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleDelete = async () => {
    if (!id) return;
    setLoading(true); setError('');
    try {
      await tradesService.deleteCost(id);
      onDeleted(); onClose();
    } catch (e: any) {
      setError(e?.data?.message || 'Failed to delete.');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{
          width: 60, height: 60, borderRadius: '50%',
          background: alpha('#f44336', 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mx: 'auto', mb: 2,
        }}>
          <DeleteIcon sx={{ color: '#f44336', fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} mb={1}>Delete Cost Entry</Typography>
        <Typography variant="body2" color="text.secondary">
          Are you sure? This action cannot be undone.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}
          sx={{ borderRadius: 2, minWidth: 100 }}>
          Cancel
        </Button>
        <Button onClick={handleDelete} variant="contained" color="error" disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: 2, minWidth: 100 }}>
          {loading ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════════════════════════════
const CostInfoPage: React.FC = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();

  const [rows, setRows]           = useState<CostInfo[]>([]);
  const [categories, setCategories] = useState<CostCategory[]>([]);
  const [totals, setTotals]       = useState<Record<string, number | null>>({});
  const [loading, setLoading]     = useState(true);

  const [search, setSearch]         = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [filterCat, setFilterCat]   = useState('');   // cost_category_id as string

  const [formOpen, setFormOpen]   = useState(false);
  const [editing, setEditing]     = useState<CostInfo | null>(null);
  const [delId, setDelId]         = useState<number | null>(null);

  // ── Fetch list ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search)    params.search           = search;
      if (dateFrom)  params.date_from        = dateFrom;
      if (dateTo)    params.date_to          = dateTo;
      if (filterCat) params.cost_category_id = filterCat;

      const res = await tradesService.listCosts(params);
      setRows(res.results);
      setTotals(res.totals || {});
    } catch { } finally { setLoading(false); }
  }, [search, dateFrom, dateTo, filterCat]);

  // Load categories once on mount
  useEffect(() => {
    tradesService.listCostCategories({ is_active: true })
      .then(r => setCategories(r.results))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (date: string | Date) => {
    let d: Date;
    
    if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}/)) {
      // Parse DD-MM-YYYY HH:mm:ss format
      const [datePart, timePart] = date.split(' ');
      const [day, month, year] = datePart.split('-');
      const timeString = timePart || '00:00:00';
      // Create date in YYYY-MM-DD format for proper parsing
      d = new Date(`${year}-${month}-${day}T${timeString}`);
    } else {
      d = new Date(date);
    }
    
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    
    return `${day}-${month}-${year}`;
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Fade in timeout={400}>
      <Box>

        {/* ── Page Header ──────────────────────────────────────────────────── */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          mb: 3, flexWrap: 'wrap', gap: 1.5,
        }}>
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{
              background: `linear-gradient(135deg, ${ACCENT} 0%, #6A1B9A 100%)`,
              backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Cost Information
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>
              Record and manage all cost / expense entries
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} size="small"
                sx={{ border: `1px solid ${alpha(ACCENT, 0.3)}`, color: ACCENT }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} size="small"
                onClick={() => { setEditing(null); setFormOpen(true); }}
                sx={{
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${ACCENT} 0%, #6A1B9A 100%)`,
                  boxShadow: `0 4px 12px ${alpha(ACCENT, 0.35)}`,
                }}>
                Add Cost
              </Button>
            )}
          </Stack>
        </Box>

        {/* ── Summary Cards ────────────────────────────────────────────────── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
          {[
            {
              label: 'Total Records',
              value: rows.length,
              icon:  <CostIcon />,
              color: ACCENT,
            },
            {
              label:   'Total Cost Amount',
              value:   Number(totals.total_amount ?? 0),
              icon:    <MoneyIcon />,
              color:   '#FF9800',
              prefix:  'Tk. ',
              decimal: true,
            },
          ].map(c => (
            <Card key={c.label} elevation={0} sx={{
              flex: 1, p: 2, borderRadius: 3,
              border: `1px solid ${alpha(c.color, 0.2)}`,
              background: alpha(c.color, 0.04),
              display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: '12px',
                background: alpha(c.color, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: c.color, flexShrink: 0,
              }}>
                {c.icon}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {c.label}
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: c.color, lineHeight: 1.2 }}>
                  {c.prefix ?? ''}{c.decimal ? fmt(c.value) : c.value}
                </Typography>
              </Box>
            </Card>
          ))}
        </Stack>

        {/* ── Filters ──────────────────────────────────────────────────────── */}
        <Card sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}` }} elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" flexWrap="wrap">

            <TextField size="small" label="From" type="date" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />

            <TextField size="small" label="To" type="date" value={dateTo}
              onChange={e => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 148, '& .MuiOutlinedInput-root': { borderRadius: 6 } }} />

            <FormControl size="small" sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 6 } }}>
              <InputLabel>Category</InputLabel>
              <Select value={filterCat} label="Category"
                onChange={e => setFilterCat(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.cost_category_id} value={String(c.cost_category_id)}>
                    {c.cost_category_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button size="small" variant="outlined"
              onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setFilterCat(''); }}
              sx={{
                borderRadius: 6, borderColor: alpha(ACCENT, 0.5), color: ACCENT,
                fontWeight: 600, px: 2,
                '&:hover': { borderColor: ACCENT, background: alpha(ACCENT, 0.06) },
              }}>
              Clear
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {rows.length} record{rows.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        </Card>

        {/* ── Table ────────────────────────────────────────────────────────── */}
        <Card elevation={0} sx={{
          borderRadius: 3, border: `1px solid ${alpha(ACCENT, 0.1)}`, overflow: 'hidden',
        }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: alpha(ACCENT, 0.04) }}>
                  {['#', 'Date', 'Category', 'Cost Name', 'Amount (Tk.)', 'Remarks', 'Created By', 'Actions'].map(h => (
                    <TableCell key={h} sx={{
                      fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary',
                      py: 1.5, borderBottom: `2px solid ${alpha(ACCENT, 0.12)}`, whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Loading */}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={32} sx={{ color: ACCENT }} />
                    </TableCell>
                  </TableRow>
                )}

                {/* Empty */}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <CostIcon sx={{ fontSize: 40, color: 'text.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">No cost entries found</Typography>
                    </TableCell>
                  </TableRow>
                )}

                {/* Data rows */}
                {!loading && rows.map((r, idx) => (
                  <TableRow key={r.cost_info_id}
                    sx={{ '&:hover': { background: alpha(ACCENT, 0.03) }, transition: 'background 0.15s' }}>

                    {/* # */}
                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.8rem' }}>
                      {idx + 1}
                    </TableCell>

                    {/* Date */}
                    <TableCell sx={{ py: 1.25, fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {formatDate(r.cost_date)}
                    </TableCell>

                    {/* Category */}
                    <TableCell sx={{ py: 1.25 }}>
                      {r.cost_category_detail ? (
                        <Chip
                          size="small"
                          label={r.cost_category_detail.cost_category_name}
                          sx={{
                            background: alpha(ACCENT, 0.08), color: ACCENT,
                            fontWeight: 600, height: 22, fontSize: '0.72rem',
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>

                    {/* Cost Name */}
                    <TableCell sx={{ py: 1.25, fontSize: '0.875rem', maxWidth: 180 }}>
                      <Typography noWrap fontWeight={600} fontSize="inherit" title={r.cost_name}>
                        {r.cost_name}
                      </Typography>
                    </TableCell>

                    {/* Amount */}
                    <TableCell sx={{ py: 1.25, textAlign: 'right', fontWeight: 700, color: '#FF9800' }}>
                      {fmt(r.cost_amount)}
                    </TableCell>

                    {/* Remarks */}
                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.78rem', maxWidth: 130 }}>
                      <Typography noWrap variant="body2" fontSize="inherit">
                        {r.remarks || '—'}
                      </Typography>
                    </TableCell>

                    {/* Created By */}
                    <TableCell sx={{ py: 1.25, color: 'text.secondary', fontSize: '0.78rem' }}>
                      {r.created_by}
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={{ py: 1.25 }}>
                      <Stack direction="row" spacing={0.5}>
                        {canUpdate && (
                          <Tooltip title="Edit">
                            <IconButton size="small"
                              onClick={() => { setEditing(r); setFormOpen(true); }}
                              sx={{ color: ACCENT, '&:hover': { background: alpha(ACCENT, 0.1) } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <IconButton size="small"
                              onClick={() => setDelId(r.cost_info_id)}
                              sx={{ color: '#f44336', '&:hover': { background: alpha('#f44336', 0.1) } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Totals footer row */}
                {rows.length > 0 && !loading && (
                  <TableRow sx={{ background: alpha(ACCENT, 0.06) }}>
                    <TableCell colSpan={4} sx={{
                      py: 1.25, fontWeight: 800, fontSize: '0.82rem', color: 'text.secondary',
                    }}>
                      TOTALS
                    </TableCell>
                    <TableCell sx={{
                      py: 1.25, textAlign: 'right', fontWeight: 800, color: '#FF9800',
                    }}>
                      {fmt(totals.total_amount ?? 0)}
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Dialogs */}
        <CostFormDialog
          open={formOpen}
          editing={editing}
          categories={categories}
          onClose={() => setFormOpen(false)}
          onSaved={fetchData}
        />
        <DeleteDialog
          open={!!delId}
          id={delId}
          onClose={() => setDelId(null)}
          onDeleted={fetchData}
        />

      </Box>
    </Fade>
  );
};

export default CostInfoPage;
